import { Reveal } from "@/components/site/reveal";

const ABOUT_IMG = "/about-caregiver.jpg";

const CHAPTERS = [
  {
    n: "01",
    title: "Companionship before checklists",
    body: "We begin with conversation, not clipboards. Before any care plan is written, we sit down, share a coffee, and listen — to routines, preferences, and the small details that make a person feel known.",
    dark: true,
  },
  {
    n: "02",
    title: "Dignity in every detail",
    body: "Help with bathing, dressing, or mobility is never rushed and never clinical. Our caregivers are trained to protect privacy and independence at every step, offering exactly as much help as is wanted — and no more.",
  },
  {
    n: "03",
    title: "Families kept close",
    body: "You should never have to wonder how Mum or Dad is doing. We keep families informed with honest updates after every visit, and we are one phone call away — day or night.",
    dark: true,
  },
  {
    n: "04",
    title: "The same familiar face",
    body: "Trust grows with consistency. We match every client with a small, dedicated team of caregivers, so the person at the door is always a familiar, welcomed friend.",
  },
];

export default function Manifesto() {
  return (
    <section id="about" data-testid="about-section" className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px w-12 bg-navy" />
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-navy">Our promise</span>
              </div>
              <h2 className="font-display font-light tracking-tight leading-[1.05] text-navy-dark text-4xl md:text-6xl">
                The Caring Hands <em className="italic text-navy">manifesto</em>
              </h2>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-navy-dark/70 max-w-md">
                Founded by Karen after caring for her own mother, our agency is built on one
                belief: great care is personal. Four principles guide every visit we make.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-12">
              <div className="relative overflow-hidden aspect-[4/5] max-w-xs">
                <img
                  data-testid="about-image"
                  src={ABOUT_IMG}
                  alt="A caregiver in a navy blue uniform walking with a smiling senior client in a bright home"
                  className="w-full h-full object-cover"
                />
                <div aria-hidden className="absolute inset-0 shadow-[inset_0_0_80px_rgba(16,43,85,0.25)]" />
              </div>
              <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.25em] text-navy-dark/50">
                Caregiver & client, at home
              </p>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col gap-10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <article
                data-testid={`manifesto-chapter-${c.n}`}
                className={`p-10 md:p-14 border transition-colors duration-500 ${
                  c.dark
                    ? "bg-navy-dark border-navy-dark text-white"
                    : "bg-white border-silver hover:border-navy/40"
                }`}
              >
                <span
                  className={`font-mono text-xs tracking-[0.3em] ${
                    c.dark ? "text-silver" : "text-navy"
                  }`}
                >
                  CHAPTER {c.n}
                </span>
                <h3
                  className={`mt-5 font-display font-light text-3xl md:text-4xl tracking-tight ${
                    c.dark ? "text-white" : "text-navy-dark"
                  }`}
                >
                  {c.title}
                </h3>
                <p
                  className={`mt-6 text-base leading-relaxed max-w-xl ${
                    c.dark ? "text-white/75" : "text-navy-dark/70"
                  }`}
                >
                  {c.body}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
